// The section shell of the hub (ui-map-interface.md §16.16, mockup round
// 092): one shape for a hub panel that holds sections. SETTINGS drew it
// first (R1731, §16.14); RECORD, MODDING, RANKS, CLAN, WARDROOM and
// SUPPORT (R1916) draw it as well.
//
// **Two forms of one model.** A panel states its sections once: a glyph, a
// word, a live summary and an optional count. A wide pane draws a box with
// the rail of the sections at the left and the page of the open section at
// the right. A narrow pane draws the sections as tiles, and a press opens
// one page a section under the sticky head ‹ PANEL. A wide pane opens on
// the first section, and a narrow pane opens on the tiles.
//
// **The page title** (R1921): every panel titles a section page as
// SETTINGS does, with the kit title `pageTitle`: the section glyph in its
// accent box at the top left, the word, and the summary under it. A wide
// pane draws it at the top of the page; a narrow page draws it in its big
// form at the top of the body, under the head ‹ PANEL, which names no
// section. A sub-page draws no title: ‹ SECTION stands in its place.
//
// **The companion column.** A panel may give each section one object to
// stand beside the box (WHERE IT SHOWS of SETTINGS, WITHIN REACH of
// RECORD). It stays while the rail changes the page. The column stands
// only while the row holds the box at its minimum, the gap and the column:
// a container query of `hub.css` on `.hs-row`, from the figures of
// `[presentation.hub_sections]`. The rule is one for every panel: one box
// minimum, and a room that stays clear of the row of the capped hub by a
// margin, so a scrollbar never takes the column away (R1910). Under that
// room the column goes, and its
// content folds where the panel says (`Fold`). The shell writes the folded
// copy and the column both, and the stylesheet shows one of them, so a
// resize needs no second render.
//
// **Three levels at most**: the dock tab, the section, and a filter row or
// a sub-page inside the pane. A sub-page names its section in the back
// form of R1751 (‹ ACHIEVEMENTS), and the rail stays. A sub-page opens at
// the top, and ‹ SECTION gives the page back at the place the reader left
// (`SectionNav.enter`, `leave`, R1919). A panel never raises a sheet with a
// scroll of its own over a page that scrolls: the sub-page is the form.
//
// **The address**: `/?tab=<panel>&section=<id>` opens a section (R1731).
// The hub hands the word to the panel's `openSection`, and the panel asks
// its `SectionNav`, which knows the ids and the old aliases of the panel.
//
// The builders are pure: they read no state and return markup. Every
// press carries `data-act`, so the hub routes it on the release (R1206).

import { oneOf } from '../../util/oneof';
import { KIT_ROOT, SHELL_DESK, SHELL_PHONE, pageTitle, railItem, sectionTiles, type NavItem } from '../options/kit';
import { textGlyph } from '../glyph';
import { backBar, esc, page, section, type PanelContext } from './panel';

/**
 * Where the companion goes when it cannot stand beside the box.
 *
 *   - `top`: a band over the page (the MODDING preview).
 *   - `foot`: a pinned foot of the page (the RANKS row of the reader).
 *   - `below`: a block after the page (WITHIN REACH of RECORD).
 *   - `rail`: a section of the rail of its own (the CLAN chat, ASK A
 *     PERSON of SUPPORT). The panel
 *     lists that section with `compact`, and the shell writes no fold.
 *   - `none`: nowhere.
 */
export type Fold = 'top' | 'foot' | 'below' | 'rail' | 'none';

/** One section, as the rail row and the tile draw it. */
export interface ShellSection {
  /** The id: the address word and the `data-id` of the press. */
  id: string;
  /** The glyph, as markup: a character, or `textGlyph`. The shell asks
   *  for the text form of a bare character itself (`shellGlyph`). */
  glyph: string;
  /** The word. */
  name: string;
  /** The live summary: the reader's own state as a figure or a status,
   *  never a description; '' while the figure is not known. */
  sum: string;
  /** The count the row and the tile carry; none at 0. */
  badge?: number;
  /** The tile spans both columns of the tiles. */
  wideTile?: boolean;
  /** The row stands in the rail only while the companion column cannot
   *  stand (fold `rail`): the column holds the same thing. The tile
   *  stands always. */
  compact?: boolean;
}

/** The companion of one section. */
export interface Companion {
  /** The label over the column, and the title of a folded card. */
  label: string;
  /** The content of the column, as markup. */
  html: string;
  /** Where it goes on a wide pane with no room for the column. */
  fold: Fold;
  /** Where it goes on a narrow page; `none` by default. `rail` means none. */
  narrow?: Fold;
  /** A shorter form for a fold, as markup (the pinned foot). The fold
   *  takes `html` where this is absent. */
  folded?: string;
}

/** The page of the open section. */
export interface ShellPage {
  /** The page itself, as markup. */
  body: string;
  /** Markup in the place of the page title that the shell writes from the
   *  section (R1921): SETTINGS passes its own, because its ACCOUNT page is
   *  no rail row and each page states a sentence, not a summary. */
  title?: string;
  /** Markup at the right of the page title, on the row of the glyph: the
   *  tier tag of a page that a tier opens (`tierTag`). */
  mark?: string;
  /** A sub-page inside the pane: `name` is its word in Dim, and `back`
   *  the act of ‹ SECTION. */
  sub?: { name: string; back: string };
  /** The companion of the page, or none. */
  companion?: Companion;
  /** Markup pinned at the foot of the wide pane (the SETTINGS legend). */
  foot?: string;
}

/** What the shell draws. */
export interface ShellSpec {
  /** The panel word: ‹ PANEL and the name of the rail. */
  title: string;
  /** The heading of the panel, as markup: `h1(...)`, or a heading of its
   *  own (the CLAN heading). The narrow page draws none. */
  heading: string;
  /** The act of a rail row and of a tile; `data-id` names the section. */
  go: string;
  /** The act of ‹ PANEL on a narrow page. */
  back: string;
  /** The word of ‹ PANEL on a narrow page, where the press leads to a
   *  page that the panel word does not name: ‹ MAIN MENU of the arcade
   *  lobby (R2071). The panel word by default. */
  backWord?: string;
  /** The sections, in the rail order. */
  sections: readonly ShellSection[];
  /** The open page id, or null for the tiles of a narrow pane. The id may
   *  name a page that is no rail row (the ACCOUNT page of SETTINGS). */
  open: string | null;
  /** The page of `open`; unused for the tiles. */
  page: ShellPage;
  /** True on a wide pane (`ctx.wide()`). */
  wide: boolean;
  /**
   * The SETTINGS form (§16.14): a box of 980 px whose rows are a form,
   * the whole box under the kit root, the WHERE IT SHOWS column of a
   * flexible width with no label, the kit head ‹ SETTINGS, and tiles whose
   * word clips. Every other panel takes the box of hub cards that grows
   * with the pane, and the head of the hub back form.
   */
  form?: boolean;
  /** The companion column of a chat: `[presentation.hub_sections]`
   *  `column_large_px` (CLAN). */
  large?: boolean;
  /** Markup under the rail rows (the commander card of SETTINGS). */
  railFoot?: string;
  /** Markup over the tiles of a narrow pane (the WARDROOM portraits). */
  rootHead?: string;
  /** Markup under the tiles of a narrow pane (WITHIN REACH of RECORD). */
  rootFoot?: string;
}

/** The grid of cards of a page: columns of at least
 *  `[presentation.hub_sections] card_min_px`, as many as the pane holds. */
export function cardGrid(...cards: string[]): string {
  return `<div class="hs-grid">${cards.filter((c) => c !== '').join('')}</div>`;
}

/** The glyph in its TEXT form (R579): ⚓, ❄, ✉ and ⌨ carry an emoji form,
 *  which iOS draws as a colour emoji in the rail, the tile and the title.
 *  A glyph that already asks for the text form (the SETTINGS sections)
 *  stays as it is. */
function shellGlyph(glyph: string): string {
  return glyph.endsWith(textGlyph('')) ? glyph : textGlyph(glyph);
}

/** The sections as the kit draws them. */
function navItems(s: ShellSpec): NavItem[] {
  return s.sections.map((sec) => ({
    glyph: shellGlyph(sec.glyph),
    name: sec.name,
    sum: sec.sum,
    press: { act: s.go, id: sec.id },
    badge: sec.badge,
    wide: sec.wideTile,
    cls: sec.compact ? 'hs-compact' : undefined,
  }));
}

/** The section `id` names, or undefined for a page that is no rail row. */
function sectionOf(s: ShellSpec, id: string | null): ShellSection | undefined {
  return s.sections.find((sec) => sec.id === id);
}

/** The companion as a section of the page: a fold above or below it. */
function foldCard(c: Companion): string {
  return section(c.label, '', c.folded ?? c.html, 'hs-fold');
}

/** The three folded places of one companion: over the page, after it,
 *  and pinned at its foot. */
function folds(c: Companion | undefined, mode: Fold | undefined): { top: string; below: string; foot: string } {
  const out = { top: '', below: '', foot: '' };
  if (c === undefined) return out;
  if (mode === 'top') out.top = foldCard(c);
  else if (mode === 'below') out.below = foldCard(c);
  else if (mode === 'foot') out.foot = `<div class="hs-fold hs-pfoot">${c.folded ?? c.html}</div>`;
  return out;
}

/** The page title of a section of hub cards, as SETTINGS draws it (R1921):
 *  the kit title, the glyph in its box, the word and the summary, under
 *  the kit root alone, so the cards keep the look of the hub sheet. `big`
 *  is the title of a narrow page, which stands in the body. The word is an
 *  `h2` in both forms: the level follows the role, not the size (R2048). */
function sectionTitle(sec: ShellSection, big: boolean, mark?: string): string {
  return `<div class="${KIT_ROOT} hs-ptitle">`
    + pageTitle({ glyph: shellGlyph(sec.glyph), name: sec.name, what: sec.sum, big, mark, heading: true }) + '</div>';
}

/** The title line of a page of hub cards on a wide pane: the page title,
 *  or ‹ SECTION on a sub-page. */
function titleLine(s: ShellSpec, sec: ShellSection | undefined): string {
  const p = s.page;
  if (p.title !== undefined) return p.title;
  if (p.sub !== undefined) return backBar(sec?.name ?? s.title, p.sub.back, p.sub.name);
  return sec === undefined ? '' : sectionTitle(sec, false, p.mark);
}

/** The column of the wide pane: WHERE IT SHOWS of the SETTINGS form, else
 *  the labelled column of hub cards with the section name. */
function column(s: ShellSpec, c: Companion | undefined, sec: ShellSection | undefined): string {
  if (c === undefined) return '';
  if (s.form) return `<div class="${KIT_ROOT} hs-where">${c.html}</div>`;
  return `<aside class="hs-side" aria-label="${esc(c.label)}"><div class="hs-lab"><span>${esc(c.label)}</span>`
    + `<span>${esc(sec?.name ?? '')}</span></div>${c.html}</aside>`;
}

/** The wide pane: the box of the rail and the page, and the column. The
 *  SETTINGS form folds no companion: its column stands, or goes. */
function desk(s: ShellSpec, open: string): string {
  const sec = sectionOf(s, open);
  const rail = navItems(s).map((it, i) => railItem(it, s.sections[i].id === open)).join('');
  const c = s.page.companion;
  const row = s.form ? 'hs-row' : `hs-row grow${s.large ? ' large' : ''}`;
  const box = s.form ? `<div class="${KIT_ROOT} ${SHELL_DESK} hset">` : '<div class="hs-box grow">';
  const nav = s.form ? '<nav class="hs-rail"' : `<nav class="${KIT_ROOT} ${SHELL_DESK} hs-rail"`;
  const f = folds(c, s.form ? undefined : c?.fold);
  return `<div class="${row}">` + box
    + `${nav} aria-label="${esc(s.title)}">${rail}${s.railFoot ?? ''}</nav><div class="hs-pane">`
    + `<div class="hs-body">${titleLine(s, sec)}${f.top}${s.page.body}${f.below}</div>`
    + `${f.foot}${s.page.foot ?? ''}</div></div>${column(s, c, sec)}</div>`;
}

/** The narrow root: the tiles, with the head and the foot blocks. */
function tiles(s: ShellSpec): string {
  const grid = sectionTiles(navItems(s));
  if (s.form) {
    return page(s.heading,
      `<div class="${KIT_ROOT} ${SHELL_PHONE} hs-root">${s.rootHead ?? ''}${grid}${s.rootFoot ?? ''}</div>`);
  }
  return page(s.heading, s.rootHead ?? '',
    `<div class="${KIT_ROOT} ${SHELL_PHONE} hs-root wrap">${grid}</div>`, s.rootFoot ?? '');
}

/** One narrow page under its sticky head. */
function phonePage(s: ShellSpec, open: string): string {
  const sec = sectionOf(s, open);
  if (s.form) {
    return page(`<div class="${KIT_ROOT} ${SHELL_PHONE} hs-page">`
      + `<div class="o-head hs-head"><button type="button" class="o-back" data-act="${esc(s.back)}">‹ ${esc(s.backWord ?? s.title)}</button></div>`
      + `<div class="hs-body">${s.page.title ?? ''}${s.page.body}</div></div>`);
  }
  const p = s.page;
  const head = p.sub !== undefined
    ? backBar(sec?.name ?? s.title, p.sub.back, p.sub.name)
    : backBar(s.backWord ?? s.title, s.back);
  const title = p.title ?? (p.sub === undefined && sec !== undefined ? sectionTitle(sec, true, p.mark) : '');
  const f = folds(p.companion, p.companion?.narrow);
  return page(`<div class="hs-page grow">${head}`
    + `<div class="hs-body">${title}${f.top}${p.body}${f.below}</div>${f.foot}</div>`);
}

/** The whole pane of a panel in the shell. A wide pane with no open page
 *  opens on the first section that is no `compact` row. */
export function shellHtml(s: ShellSpec): string {
  if (s.wide) {
    const open = s.open ?? (s.sections.find((sec) => !sec.compact) ?? s.sections[0])?.id ?? '';
    return page(s.heading, desk(s, open));
  }
  return s.open === null ? tiles(s) : phonePage(s, s.open);
}

/** The two presses of the shell itself: the act of a rail row and of a
 *  tile, and the act of ‹ PANEL of a narrow page (`ShellSpec` `go` and
 *  `back`). */
export interface ShellKeys {
  go: string;
  back: string;
}

/**
 * Takes the two presses of the shell itself (§16.16), and answers false
 * for every other press. `keys.go` opens the section the `data-id` of the
 * press names; `keys.back` leads back to the tiles of a narrow pane. A
 * move plays its cue and redraws. `moved` runs between the move and the
 * cue, with the page that opens (null for the tiles) and, for a rail
 * press, the page that stood before it (null for ‹ PANEL), so the panel
 * drops what the old page held.
 */
export function shellPress<T extends string>(
  nav: SectionNav<T>, ctx: Pick<PanelContext, 'wide' | 'play' | 'refresh'>, keys: ShellKeys,
  action: string, el: HTMLElement, moved?: (to: T | null, from: T | null) => void,
): boolean {
  if (action === keys.go) {
    const wide = ctx.wide();
    const from = nav.shown(wide);
    const to = nav.named(el.dataset.id);
    if (to === undefined || !nav.go(to, wide)) return true;
    moved?.(to, from);
    ctx.play('sfx-menu-tab');
  } else if (action === keys.back) {
    nav.back();
    moved?.(null, null);
    ctx.play('sfx-menu-back');
  } else {
    return false;
  }
  ctx.refresh();
  return true;
}

/**
 * Which page of a panel in the shell stands open, and the address words
 * it takes (§16.16). One per panel.
 *
 * `ids` lists every page the panel has: the rail sections and a page that
 * is no rail row (the ACCOUNT page of SETTINGS). `aliases` maps an old
 * word to an id, so an old address or an old `ctx.go` keeps working.
 * `first` is the page a wide pane opens on. `T` is the id type of the
 * panel, so `shown` and `named` answer an id the panel can switch on.
 */
export class SectionNav<T extends string = string> {
  private at: T | null = null;
  private pending: T | null = null;
  private top = false;
  /** True between `open` and `close`: a word asked now opens at once. */
  private live = false;
  /** The pane the last wire scrolled. */
  private root: HTMLElement | null = null;
  /** The place of the page under an open sub-page (R1919). */
  private kept: number | null = null;
  /** The place the next wire gives the pane. */
  private place: number | null = null;

  constructor(private readonly o: {
    ids: () => readonly T[];
    first: () => T;
    aliases?: Readonly<Record<string, T>>;
  }) {}

  /** The page `id` names, or undefined for a word that names no page of
   *  the panel: the `data-id` of a rail row or of a tile. */
  named(id: string | null | undefined): T | undefined {
    return oneOf(this.o.ids(), id);
  }

  /** The id `word` names, directly or through an alias, or undefined. */
  read(word: string): T | undefined {
    return this.named(Object.hasOwn(this.o.aliases ?? {}, word) ? this.o.aliases![word] : word);
  }

  /** True while the panel stands open (between `open` and `close`). */
  get isOpen(): boolean {
    return this.live;
  }

  /** The address or a press elsewhere named `word`: the next `open`
   *  opens on it, or the page opens at once while the panel stands open,
   *  so a press that names a section of the open tab acts now (R2237).
   *  A word the panel does not know changes nothing. */
  ask(word: string): boolean {
    const id = this.read(word);
    if (id === undefined) return false;
    if (!this.live) {
      this.pending = id;
      return true;
    }
    this.moveTo(id);
    return true;
  }

  /** The panel opened: the asked page, else `fallback` (null: the first
   *  section of a wide pane, the tiles of a narrow one). */
  open(fallback: T | null = null): void {
    this.at = this.pending ?? fallback;
    this.pending = null;
    this.top = true;
    this.live = true;
  }

  /** The panel closed. */
  close(): void {
    this.live = false;
    this.at = null;
    this.pending = null;
    this.root = null;
    this.kept = null;
    this.place = null;
  }

  /** The page the pane shows: the open page, or on a wide pane the first
   *  section. Null is the tiles of a narrow pane. */
  shown(wide: boolean): T | null {
    return this.at ?? (wide ? this.o.first() : null);
  }

  /** Opens the page `id`. False where it already stands, or where the
   *  panel has no such page. */
  go(id: string, wide: boolean): boolean {
    const to = this.named(id);
    if (to === undefined || to === this.shown(wide)) return false;
    this.moveTo(to);
    return true;
  }

  /** ‹ PANEL: back to the tiles of a narrow pane. */
  back(): void {
    this.moveTo(null);
  }

  /** Stands the pane on `id` (null: the tiles), at the top, with no kept
   *  place of a sub-page. */
  private moveTo(id: T | null): void {
    this.at = id;
    this.top = true;
    this.kept = null;
    this.place = null;
  }

  /** A sub-page opens over the page of the section (R1919): the place the
   *  reader left is kept, and the sub-page opens at the top. `from` names
   *  the place where the press came from another page (the tiles). */
  enter(from?: number): void {
    this.kept = from ?? this.root?.scrollTop ?? 0;
    this.place = null;
    this.top = true;
  }

  /** ‹ SECTION of a sub-page: the next wire gives the page back at the
   *  place `enter` kept. */
  leave(): void {
    this.place = this.kept ?? 0;
    this.kept = null;
    this.top = false;
  }

  /** After a change of page, the next wire scrolls the pane to the top,
   *  because a page is a new page; after ‹ SECTION, to the kept place. */
  scroll(root: HTMLElement): void {
    this.root = root;
    if (this.place !== null) {
      root.scrollTop = this.place;
      this.place = null;
      this.top = false;
      return;
    }
    if (!this.top) return;
    root.scrollTop = 0;
    this.top = false;
  }
}
